// Documento do Contrato — [título do modelo] + [dados do cliente/veículos/
// valores] + [texto do modelo] + [prova de assinatura: mapa/foto/assinaturas].
// Usado em dois lugares (pedido 2026-09-11): visível dentro do popup "Ver
// contrato" (ContractPreviewDialog, ver routes/users.tsx) e, pro botão
// "Baixar (PDF)" do mesmo popup, escondido dentro de ClientContractPrintReport
// — mesma técnica de route-history-center.tsx#PrintReport: sem biblioteca de
// PDF, um DOM A4 próprio só visível durante impressão (window.print()),
// renderizado via portal direto em document.body pra nunca herdar um
// ancestral "position: fixed" do Dialog (causa conhecida de página em branco
// no Chrome/"Salvar como PDF").
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { createStaticMapsUrl } from "@vis.gl/react-google-maps";
import logoAsset from "@/assets/gps-drive-logo-v3.png.asset.json";
import { resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";
import { useContractCompany } from "@/hooks/queries";
import { vehicleMetadataService } from "@/services/vehicle-metadata.service";
import type { TraccarDeviceRef } from "@/services/vehicle-permissions.service";
import type { Plan } from "@/services/gpsdrive-plans.service";
import type { ClientContract } from "@/services/gpsdrive-client-contract.service";
import { ContractSignatureCertificate, type CertificateSigner } from "@/components/contract-signature-certificate";

export interface ClientContractDocumentProps {
  clientName: string;
  cpfCnpj: string;
  phone: string;
  email: string;
  address: string;
  dueDay: string;
  vehicles: TraccarDeviceRef[];
  plans: Plan[];
  template: { title: string; content: string } | null;
  contractValue: string;
  recurrenceValue: string;
  contract: ClientContract;
  signatory: CertificateSigner | null;
  // Vários sócios/testemunhas (2026-10-14) — quando vem, manda sobre signatory.
  signatories?: CertificateSigner[];
  className?: string;
}

// Conteúdo puro do contrato — sem a classe print-only, pra poder aparecer
// visível normalmente dentro do popup "Ver contrato" (fundo branco/texto
// preto de propósito mesmo lá: é a cara de um documento/papel, não da tela).
export function ClientContractDocument({
  clientName,
  cpfCnpj,
  phone,
  email,
  address,
  dueDay,
  vehicles,
  plans,
  template,
  contractValue,
  recurrenceValue,
  contract,
  signatory,
  signatories,
  className,
}: ClientContractDocumentProps) {
  const signers = signatories ?? (signatory ? [signatory] : []);
  // Cabeçalho = Administração → Contrato → Dados da empresa (2026-10-14),
  // não mais "GPS Drive" fixo.
  const { data: company } = useContractCompany();
  const companyLogo = company?.logoUrl ? resolveGpsDriveUrl(company.logoUrl) : company ? null : logoAsset.url;
  const companyLines = company
    ? [
        company.document && `CNPJ/CPF: ${company.document}`,
        [company.phone, company.email].filter(Boolean).join(" · "),
        company.address,
        company.website,
      ].filter((l): l is string => !!l)
    : [];
  const deviceIds = (contract.vehicles?.length ?? 0) > 0 ? [] : vehicles.map((v) => v.id);
  // Placa já vem junto de `vehicles` (attributes.plate, ver
  // vehiclePermissionsService#vehiclesForCompany/vehiclesForUser); só o
  // Plano exige uma busca própria (vehicle_metadata.planId não está em
  // nenhuma resposta em lote hoje — ver comentário no schema). Volume baixo
  // (veículos de UM cliente, não da frota inteira), então N chamadas
  // individuais em paralelo é suficiente, sem precisar de endpoint em lote
  // novo só para isto.
  const { data: vehicleMetas } = useQuery({
    queryKey: ["contract-print-vehicle-plans", deviceIds],
    queryFn: () =>
      Promise.all(
        deviceIds.map((id) =>
          vehicleMetadataService.get(id).catch(() => null),
        ),
      ),
    enabled: deviceIds.length > 0,
  });
  const planNameForDevice = (deviceId: number): string => {
    const meta = vehicleMetas?.find((m) => m?.traccarDeviceId === deviceId);
    if (!meta?.planId) return "Sem plano";
    return plans.find((p) => p.id === meta.planId)?.name ?? "Sem plano";
  };

  const googleMapsApiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ?? "";
  const hasSelfServiceProof =
    contract.signedMethod === "link" &&
    contract.signedLatitude != null &&
    contract.signedLongitude != null;
  const mapUrl =
    hasSelfServiceProof && googleMapsApiKey
      ? createStaticMapsUrl({
          apiKey: googleMapsApiKey,
          width: 640,
          height: 320,
          mapType: "roadmap" as google.maps.MapTypeId,
          markers: [
            {
              location: { lat: contract.signedLatitude!, lng: contract.signedLongitude! },
              color: "0xdc2626",
            },
          ],
        })
      : null;
  const now = new Date();

  return (
    <div className={className ?? ""} style={{ background: "white", color: "black", padding: 0 }}>
      <div className="flex items-start justify-between gap-4 rounded-t-lg pb-3" style={{ borderBottom: "3px solid #1e293b" }}>
        <div className="flex min-w-0 items-center gap-3">
          {companyLogo && <img src={companyLogo} alt={company?.name ?? ""} className="h-14 w-auto max-w-[180px] object-contain" />}
          <div className="min-w-0">
            <div className="text-lg font-bold tracking-tight">{company?.name || template?.title || "Contrato"}</div>
            {companyLines.map((l, i) => (
              <div key={i} className="text-[11px] leading-snug text-gray-600">{l}</div>
            ))}
            {company?.name && <div className="mt-0.5 text-xs font-medium text-gray-500">{template?.title ?? "Contrato"}</div>}
          </div>
        </div>
        <div className="text-right text-xs text-gray-600">
          <div>Emitido em {now.toLocaleDateString("pt-BR")}</div>
          <div>às {now.toLocaleTimeString("pt-BR")}</div>
        </div>
      </div>

      <div className="mt-4 rounded-lg p-3" style={{ border: "1px solid #e5e7eb" }}>
        <div className="mb-2 text-xs font-bold uppercase text-gray-500">Dados do cliente</div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
          <div><b>Nome completo:</b> {clientName || "—"}</div>
          <div><b>CPF/CNPJ:</b> {cpfCnpj || "—"}</div>
          <div><b>Telefone:</b> {phone || "—"}</div>
          <div><b>Email:</b> {email || "—"}</div>
          <div className="col-span-2"><b>Endereço:</b> {address || "—"}</div>
          <div><b>Vencimento:</b> {dueDay ? `Todo dia ${dueDay}` : "—"}</div>
        </div>

        {(contract.vehicles?.length ?? 0) > 0 ? (
          <table className="mt-3 w-full border-collapse text-xs">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="border-b border-gray-200 pb-1 pr-2 font-semibold">Placa</th>
                <th className="border-b border-gray-200 pb-1 pr-2 font-semibold">Veículo</th>
                <th className="border-b border-gray-200 pb-1 pr-2 font-semibold">Ano</th>
                <th className="border-b border-gray-200 pb-1 pr-2 font-semibold">Cor</th>
                <th className="border-b border-gray-200 pb-1 pr-2 font-semibold">Plano</th>
                <th className="border-b border-gray-200 pb-1 text-right font-semibold">Mensalidade</th>
              </tr>
            </thead>
            <tbody>
              {contract.vehicles.map((v, i) => (
                <tr key={i}>
                  <td className="border-b border-gray-100 py-1 pr-2">{v.plate || "—"}</td>
                  <td className="border-b border-gray-100 py-1 pr-2">{v.model || "—"}</td>
                  <td className="border-b border-gray-100 py-1 pr-2">{v.year || "—"}</td>
                  <td className="border-b border-gray-100 py-1 pr-2">{v.color || "—"}</td>
                  <td className="border-b border-gray-100 py-1 pr-2">{v.plan || "—"}</td>
                  <td className="border-b border-gray-100 py-1 text-right">
                    {v.monthlyValue != null
                      ? v.monthlyValue.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : vehicles.length > 0 && (
          <table className="mt-3 w-full border-collapse text-xs">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="border-b border-gray-200 pb-1 pr-2 font-semibold">Placa</th>
                <th className="border-b border-gray-200 pb-1 font-semibold">Plano</th>
              </tr>
            </thead>
            <tbody>
              {vehicles.map((v) => (
                <tr key={v.id}>
                  <td className="border-b border-gray-100 py-1 pr-2">
                    {(v.attributes?.plate as string | undefined) || v.name}
                  </td>
                  <td className="border-b border-gray-100 py-1">{planNameForDevice(v.id)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="mt-3 flex gap-6 text-xs">
          <div><b>Valor da Recorrência:</b> R$ {recurrenceValue || "0,00"}</div>
          <div><b>Valor Total:</b> R$ {contractValue || "0,00"}</div>
        </div>
      </div>

      <div className="mt-4 whitespace-pre-wrap rounded-lg p-3 text-xs leading-relaxed" style={{ border: "1px solid #e5e7eb" }}>
        {template?.content || "Nenhum modelo de contrato selecionado."}
      </div>

      {hasSelfServiceProof && (
        <div className="mt-4 rounded-lg p-3" style={{ border: "1px solid #e5e7eb" }}>
          <div className="mb-2 text-xs font-bold uppercase text-gray-500">
            Comprovação da assinatura por autoatendimento
          </div>
          <div className="flex flex-wrap gap-3">
            <SignatureLocationMap
              lat={contract.signedLatitude}
              lng={contract.signedLongitude}
              staticMapUrl={mapUrl}
            />
            {contract.signedPhotoUrl && (
              <img
                src={resolveGpsDriveUrl(contract.signedPhotoUrl)}
                alt="Foto tirada pelo cliente na assinatura"
                className="h-40 w-32 rounded-md border border-gray-200 object-cover"
              />
            )}
          </div>
          <div className="mt-2 text-xs text-gray-600">
            {contract.signedAt && <div>Assinado em {new Date(contract.signedAt).toLocaleString("pt-BR")}</div>}
            {contract.signedLatitude != null && contract.signedLongitude != null && (
              <div>
                Localização: {contract.signedLatitude.toFixed(6)}, {contract.signedLongitude.toFixed(6)}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-2 gap-x-8 gap-y-10 text-xs">
        <div className="text-center">
          <div className="border-t border-gray-400 pt-1">
            {contract.signedMethod === "link" && contract.signedByName ? contract.signedByName : clientName || "—"}
          </div>
          <div className="mt-0.5 text-gray-500">Assinatura do Cliente</div>
        </div>
        {(signers.length ? signers : [null]).map((s, i) => (
          <div key={i} className="text-center">
            <div className="border-t border-gray-400 pt-1">{s?.name ?? ""}</div>
            <div className="mt-0.5 text-gray-500">
              Assinatura {s?.role === "testemunha" ? "da Testemunha" : "do Sócio"}
            </div>
          </div>
        ))}
      </div>

      {/* Página própria só de assinaturas (2026-10-14). */}
      <ContractSignatureCertificate
        contract={contract}
        title={template?.title ?? "Contrato"}
        clientName={clientName}
        clientDoc={cpfCnpj}
        signers={signers}
        companyLogo={companyLogo}
        companyName={company?.name || null}
      />
    </div>
  );
}

// Mapa da localização da assinatura — pedido 2026-09-11 ("o mapa não
// aparece, fica em branco"): a Google Maps Static API precisa estar
// habilitada à parte pra essa chave no Google Cloud Console (mesma
// ressalva já documentada em route-history-center.tsx#buildPrintMapUrl) —
// quando não está, a imagem retorna 403 e o <img> simplesmente não
// renderiza nada. Fallback automático: embed clássico do Google Maps
// (`/maps?...&output=embed`), que não exige NENHUMA API habilitada — só
// funciona na tela (iframe não é confiável em impressão), por isso o
// texto "Localização: lat, lng" + link continuam sempre visíveis embaixo
// independente de qual dos dois renderizou.
function SignatureLocationMap({
  lat,
  lng,
  staticMapUrl,
}: {
  lat: number | null;
  lng: number | null;
  staticMapUrl: string | null;
}) {
  const [staticFailed, setStaticFailed] = useState(false);
  if (lat == null || lng == null) return null;

  if (staticMapUrl && !staticFailed) {
    return (
      <img
        src={staticMapUrl}
        alt="Localização da assinatura"
        onError={() => setStaticFailed(true)}
        className="h-40 w-[320px] rounded-md border border-gray-200 object-cover"
      />
    );
  }

  return (
    <iframe
      title="Localização da assinatura"
      src={`https://www.google.com/maps?q=${lat},${lng}&output=embed`}
      className="h-40 w-[320px] rounded-md border-0"
      loading="lazy"
    />
  );
}

// Cópia escondida do documento (portal em document.body) usada pelo botão
// "Baixar (PDF)" via printClientContract(). Sempre display:none na página.
export function ClientContractPrintReport(props: ClientContractDocumentProps & { open: boolean }) {
  const { open, ...docProps } = props;
  if (!open) return null;
  return createPortal(
    <div style={{ display: "none" }}>
      <ClientContractDocument {...docProps} className="cc-print-only" />
    </div>,
    document.body,
  );
}

// "Baixar (PDF)" (pedido 2026-10-14: "o PDF está saindo tudo branco"). O
// truque antigo (window.print() da página com `body * { visibility:
// hidden }`) saía em branco com o Dialog aberto. Agora: copia o HTML do
// documento + as folhas de estilo da página para um iframe só dele e
// imprime o iframe — mesmo caminho de lib/reports-export.ts#printReport,
// que já funciona nos relatórios. Espera as imagens (logo, mapa, selfie).
export function printClientContract(title = "Contrato") {
  const docs = document.querySelectorAll<HTMLElement>(".cc-print-only");
  const source = docs[docs.length - 1];
  if (!source) return;

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
  document.body.appendChild(iframe);
  const win = iframe.contentWindow;
  const doc = iframe.contentDocument ?? win?.document;
  if (!win || !doc) {
    iframe.remove();
    return;
  }
  const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
    .map((el) => {
      if (el instanceof HTMLLinkElement) return `<link rel="stylesheet" href="${el.href}">`;
      return el.outerHTML;
    })
    .join("\n");
  const esc = (t: string) => t.replace(/[<>&"]/g, (c) => `&#${c.charCodeAt(0)};`);
  doc.open();
  doc.write(`<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8" />
<title>${esc(title)}</title>
${styles}
<style>
  html, body { background: #fff !important; color: #000 !important; margin: 0; height: auto !important; overflow: visible !important; }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .cc-print-only { display: block !important; visibility: visible !important; position: static !important; }
  @page { size: A4; margin: 14mm; }
</style>
</head>
<body>${source.outerHTML}</body>
</html>`);
  doc.close();

  let printed = false;
  const doPrint = () => {
    if (printed) return;
    printed = true;
    try {
      win.focus();
      win.print();
    } finally {
      setTimeout(() => iframe.remove(), 60_000);
    }
  };
  const waitFor: Promise<unknown>[] = Array.from(doc.images)
    .filter((img) => !img.complete)
    .map((img) => new Promise((r) => {
      img.addEventListener("load", r, { once: true });
      img.addEventListener("error", r, { once: true });
    }));
  // Folhas de estilo externas (Tailwind) precisam carregar, senão sai sem layout.
  for (const link of Array.from(doc.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'))) {
    waitFor.push(new Promise((r) => {
      link.addEventListener("load", r, { once: true });
      link.addEventListener("error", r, { once: true });
    }));
  }
  // Fonte cursiva das assinaturas (certificado) só baixa quando usada —
  // espera ela, senão o PDF sai com a fonte genérica.
  void Promise.all(waitFor)
    .then(() => doc.fonts?.load('26px "Sacramento"').catch(() => undefined))
    .then(() => setTimeout(doPrint, 150));
  setTimeout(doPrint, 10_000);
}
